import "@fontsource-variable/inter";
import "@fontsource-variable/dancing-script";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { App } from "./app/App";
import { installAutoUpdate } from "./app/autoUpdate";
import { installWallKeyboard } from "./app/wallKeyboard";
import { installWallTouch } from "./app/wallTouch";
import { ApiRequestError } from "./data/api";
import { DirectionsHost } from "./ui/Directions";
import { ErrorBoundary, reloadForNewVersion } from "./ui/ErrorBoundary";
import { installKeepTyping } from "./ui/keepTyping";
import { installLinkGuard, OutsideLinkHost } from "./ui/OutsideLink";
import { TouchKeyboard } from "./ui/TouchKeyboard";
import { SnowReportHost } from "./widgets/outdoors/SnowReport";
import "./styles/tokens.css";
import "./styles/app.css";
import "./styles/mobile.css";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Back off gently on failures; never hammer a struggling provider.
      retry: (count, err) => !(err instanceof ApiRequestError && [400, 401, 403, 404, 429].includes(err.status)) && count < 2,
      retryDelay: (attempt) => Math.min(30_000, 2_000 * 2 ** attempt),
      // Screens not on show keep their data in memory (and fresh, see
      // data/keepWarm.tsx) so switching screens never waits on the network.
      gcTime: 12 * 60 * 60_000,
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
      networkMode: "online",
    },
  },
});

// Kinmantle only works over https: its sign-in and demo cookies are Secure,
// so a browser left on http://… (Edge doesn't always switch by itself) would
// never keep them. Home-network dev servers (npm run dev:lan) stay on http.
const LOCAL_HOST = /^(localhost|127\.|\[::1\]|10\.|192\.168\.|172\.(1[6-9]|2\d|3[01])\.)|\.local$/;
if (location.protocol === "http:" && !LOCAL_HOST.test(location.hostname)) {
  location.replace(`https://${location.host}${location.pathname}${location.search}${location.hash}`);
} else {
  installLinkGuard();
  installKeepTyping();
  installWallKeyboard();
  installWallTouch();
  installAutoUpdate();
  // A lazy piece from an older deploy is gone from the server: load the current version.
  window.addEventListener("vite:preloadError", (e) => {
    if (reloadForNewVersion((e as Event & { payload?: unknown }).payload)) e.preventDefault();
  });
  createRoot(document.getElementById("root")!).render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <ErrorBoundary>
          <App />
          <SnowReportHost />
          <DirectionsHost />
          <OutsideLinkHost />
          <TouchKeyboard />
        </ErrorBoundary>
      </QueryClientProvider>
    </StrictMode>,
  );
}
